'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { XYCHART_SERIES_TYPE, type XYChartSeriesLayout } from './xychart-types';
import type { MeasuredLineLengths } from './xychart-canvas-model';

export interface XYChartLinePathNodeInput {
  seriesIndex: number;
  node: SVGPathElement | null;
}

export interface UseXYChartLinePathLengthsInput {
  series: XYChartSeriesLayout[];
}

export interface UseXYChartLinePathLengthsResult {
  pathLengths: MeasuredLineLengths;
  setLinePathNode: (input: XYChartLinePathNodeInput) => void;
}

export function useXYChartLinePathLengths({
  series,
}: UseXYChartLinePathLengthsInput): UseXYChartLinePathLengthsResult {
  const linePathRefs = useRef<Record<number, SVGPathElement | null>>({});
  const [pathLengths, setPathLengths] = useState<MeasuredLineLengths>({});

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const measured: MeasuredLineLengths = {};

      series.forEach((seriesLayout) => {
        if (seriesLayout.type !== XYCHART_SERIES_TYPE.Line) return;

        const node = linePathRefs.current[seriesLayout.seriesIndex];
        if (!node) return;

        const length = node.getTotalLength();
        if (Number.isFinite(length)) {
          measured[seriesLayout.seriesIndex] = length;
        }
      });

      setPathLengths(measured);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [series]);

  const setLinePathNode = useCallback(({ seriesIndex, node }: XYChartLinePathNodeInput) => {
    linePathRefs.current[seriesIndex] = node;
  }, []);

  return { pathLengths, setLinePathNode };
}
